<template>
  <BaseLocationSection
    :id="id"
    :model-value="stops"
    :parent-entity="shipment"
    :mileages="mileages"
    :line-items="shipment.lineItems"
    :hide-notes="hideNotes"
    :show-limited-location="showLimitedLocation"
  />
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import type { PropType, Ref } from 'vue';
import { ref, toRef, toRefs, watch } from 'vue';

import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Shipment, StopMileageResponse } from '@/model';

import BaseLocationSection from '@/components/location/BaseLocationSection.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Object as PropType<Shipment>,
    default: () => new Shipment(),
  },
  hideNotes: {
    type: Boolean,
    default: () => false,
  },
  showLimitedLocation: {
    type: Boolean,
    default: () => false,
  },
});
const { id, shipment, hideNotes, showLimitedLocation } = toRefs(props);

const stops = toRef(() => shipment.value.stops);

const mileages: Ref<Loadable<StopMileageResponse[]>> = ref({ loading: false });
const mileageCancelToken: Ref<CancelTokenSource | undefined> = ref();

const findMileages = async () => {
  mileageCancelToken.value?.cancel();
  mileageCancelToken.value = axios.CancelToken.source();

  try {
    mileages.value.error = undefined;
    mileages.value.loading = true;
    mileages.value.data = await api.route.findMileage(
      stops.value,
      mileageCancelToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching mileage', e);
    mileages.value.error = e;
  } finally {
    mileages.value.loading = false;
  }
};

watch(
  stops,
  (newVal) => {
    if (newVal.length) {
      findMileages();
    }
  },
  { immediate: true },
);
</script>
